import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import { uploadRecordingS3, listCampaignsApi, fetchCampaign, triggerbulk, createJobMetadata, fetchInteractionCalls, previewInteractionCalls, singleGenesysUpload, fetchGenesysFilters } from '../api/api';
import GenesysPreviewModal from '../components/GenesysPreviewModal';

// Queue name to ID mapping (hardcoded for now)
const QUEUE_MAPPINGS = {
  'helpdesk': '',
  // Add more queue mappings here as needed
};

function detectMeta(filename) {
  const agentMatch = filename.match(/_AGENT_(.*?)_/i);
  const guidMatch  = filename.match(/_GUID_(.*?)_/i);
  const dtMatch    = filename.match(/_DT_([\dT\-]+)/i);
  return {
    agent:            agentMatch ? agentMatch[1] : '',
    guid:             guidMatch  ? guidMatch[1]  : '',
    datetime:         dtMatch    ? dtMatch[1].replace(/(\d{4}-\d{2}-\d{2}T\d{2})-(\d{2})/, '$1:$2') : '',
    agentDetected:    !!agentMatch,
    guidDetected:     !!guidMatch,
    datetimeDetected: !!dtMatch,
  };
}

function prepareUploadFile(file, campaignId, templateName, username, jobId, meta = {}) {
  const sanitize = (val) => (val || '').trim().replace(/\s+/g, '-').replace(/_/g, '');
  const ext = file.name.substring(file.name.lastIndexOf('.'));

  const agent = sanitize(meta.agent);
  const guid  = sanitize(meta.guid);
  const hasAnyMeta = agent || guid || (meta.datetime && meta.datetime.trim());

  let newFilename;

  if (hasAnyMeta) {
    // Metadata provided — build filename from tokens only, drop original name
    newFilename = `CUST_${campaignId}_TEMP_${templateName}_ASSIGN_${username}_JOB_${jobId}`;
    if (guid)  newFilename += `_GUID_${guid}`;
    if (agent) newFilename += `_AGENT_${agent}`;
    if (meta.datetime && meta.datetime.trim()) {
      // "2025-01-15T09:30" → "2025-01-15T09-30-00"
      const dt = meta.datetime.trim().replace(/:/g, '-')
        + (meta.datetime.trim().length === 16 ? '-00' : '');
      newFilename += `_DT_${dt}`;
    }
    newFilename += ext;
  } else {
    // No metadata — preserve original filename with prefix
    const lastDotIndex = file.name.lastIndexOf('.');
    const nameWithoutExt = file.name.substring(0, lastDotIndex).replace(/\./g, '');
    const finalName = nameWithoutExt + '_' + ext;
    newFilename = `CUST_${campaignId}_TEMP_${templateName}_ASSIGN_${username}_JOB_${jobId}_${finalName}`;
  }

  return new File([file], newFilename, {
    type: file.type,
    lastModified: file.lastModified,
  });
}

const UploadRecordings = ({ setAlert,username }) => {
  const navigate = useNavigate();
  const { mutate } = useSWRConfig();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [campaignError, setCampaignError] = useState(null);
  const [scorecards, setScorecards] = useState([]);
  const [selectedScorecard, setSelectedScorecard] = useState(null);
  const [loadingScorecards, setLoadingScorecards] = useState(false);
  const [scorecardError, setScorecardError] = useState(null);
  const [convoId, setConvoId] = useState('')

  // Genesys fetch mode states
  const [uploadMode, setUploadMode] = useState('file'); // 'file' or 'genesys'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [fetchingCalls, setFetchingCalls] = useState(false);

  // Preview modal states
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const [fetchingFilters, setFetchingFilters] = useState(false);
  const [agentOptions, setAgentOptions] = useState([]);
  const [wrapUpCodeOptions, setWrapUpCodeOptions] = useState([]);

  const [agentSearch, setAgentSearch] = useState('');
  const [wrapupSearch, setWrapupSearch] = useState('');
  const [isAgentOpen, setIsAgentOpen] = useState(false);
  const [isWrapupOpen, setIsWrapupOpen] = useState(false);

  const [queueOptions, setQueueOptions] = useState([]);
  const [queueSearch, setQueueSearch] = useState('');
  const [isQueueOpen, setIsQueueOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Filter lists based on search input
  const filteredAgents = agentOptions.filter(agent => 
    agent.name.toLowerCase().includes(agentSearch.toLowerCase())
  );

  const filteredWrapupCodes = wrapUpCodeOptions.filter(code => 
    code.name.toLowerCase().includes(wrapupSearch.toLowerCase())
  );

  // Filter states
  const [filters, setFilters] = useState({
    queueIds: [],
    direction: '',
    userIds: [],
    wrapUpCodes: [],
    ani: '',
    dnis: '',
    divisionIds: []
  });

  // Filter input helpers
  const [userIdInput, setUserIdInput] = useState('');
  const [wrapUpCodeInput, setWrapUpCodeInput] = useState('');
  const [divisionIdInput, setDivisionIdInput] = useState('');

  // Temporary hardcoded wrap-up codes until permissions are granted
  const HARDCODED_WRAPUP = [
    { wrapupcodesId: '969106c8-c0e6-4f88-b778-e72f3ff3828f', Name: 'MTN Sale-downsell' }, 
    { wrapupcodesId: '8e233dcb-0f19-46e3-b5ee-fec7fac296ce', Name: 'MTN Sale-No coverage' },
    { wrapupcodesId: 'b112032d-0db2-455c-af97-6c0a718c6a4c', Name: 'Sale Helivac' },
    { wrapupcodesId: 'b1679a78-785c-4a64-921b-fba26aed2859', Name: 'Sale Motor Warranty' },
    { wrapupcodesId: '77e15407-0c58-4209-99c1-2f699e5c2cf7', Name: 'Sale MW & Helivac' },
    { wrapupcodesId: 'b2b8b7bd-e310-4083-9b21-16c122f547a4', Name: 'Multiple Sale' },
    { wrapupcodesId: 'bec4888c-95cb-48ee-854f-b88256156ba0', Name: 'Sale' },
  ];

  useEffect(() => {
    const fetchCampaigns = async () => {
      setLoadingCampaigns(true);
      try {
        const res = await listCampaignsApi();
        const items = res.campaigns || [];
        const options = items.map((c) => ({
          label: c.campaignName,
          value: c.campaignName,
        }));
        setCampaigns(options);
      } catch (error) {
        console.error('Error fetching campaigns:', error);
        setCampaignError('Failed to load campaigns.');
      } finally {
        setLoadingCampaigns(false);
      }
    };
    fetchCampaigns();
  }, []);
  useEffect(() => {
    if (!selectedCampaign) {
      setScorecards([]);
      setSelectedScorecard(null);
      return;
    }

    const fetchScorecards = async () => {
      setLoadingScorecards(true);
      setScorecardError(null);
      try {
        const res = await fetchCampaign(selectedCampaign.value);
        const names = res.templates || [];
        setScorecards(names.map((n) => ({ label: n, value: n })));
      } catch (err) {
        console.error('Error loading scorecards:', err);
        setScorecardError('Failed to load scorecards.');
      } finally {
        setLoadingScorecards(false);
      }
    };

    fetchScorecards();
  }, [selectedCampaign]);

// Commented out while waiting for full permisions to fetch agents, queues, and wrap-up codes from Genesys. Using hardcoded wrap-up codes for now.  

useEffect(() => {
  const loadFilters = async () => {
    setFetchingFilters(true);
    try {
      const res = await fetchGenesysFilters('all');
      if (res.success) {
        setQueueOptions(res.data.queues || []);
        setAgentOptions(res.data.agents || []);
        setWrapUpCodeOptions(res.data.wrapUpCodes || []);
      }
    } catch (error) {
      console.error('Error loading Genesys filters:', error);
    } finally {
      setFetchingFilters(false);
    }
  };
  loadFilters();
}, []);

  // Handle file drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    const audioFiles = droppedFiles.filter(file =>
      file.type.startsWith('audio/') ||
      file.name.toLowerCase().endsWith('.wav') ||
      file.name.toLowerCase().endsWith('.mp3') ||
      file.name.toLowerCase().endsWith('.m4a') ||
      file.name.toLowerCase().endsWith('.flac')
    );

    // Filter out files with names longer than 200 characters
    const validFiles = audioFiles.filter(file => file.name.length <= 200);
    const rejectedFileCount = audioFiles.length - validFiles.length;
    const nonAudioCount = droppedFiles.length - audioFiles.length;

    if (nonAudioCount > 0 || rejectedFileCount > 0) {
      const messages = [];
      if (nonAudioCount > 0) {
        messages.push(`${nonAudioCount} non-audio file(s) skipped`);
      }
      if (rejectedFileCount > 0) {
        messages.push(`${rejectedFileCount} file(s) with names longer than 200 characters rejected`);
      }
      setAlert({
        variant: 'warning',
        heading: 'Some Files Rejected',
        text: messages.join('. ') + '.'
      });
    }

    setFiles(prev => [...prev, ...validFiles.map(file => ({
      file,
      id: Date.now() + Math.random(),
      status: 'pending',
      progress: 0,
      meta: detectMeta(file.name),
    }))]);
  }, [setAlert]);

  // Handle file input change
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    // Filter out files with names longer than 200 characters
    const validFiles = selectedFiles.filter(file => file.name.length <= 200);
    const rejectedFileCount = selectedFiles.length - validFiles.length;

    if (rejectedFileCount > 0) {
      setAlert({
        variant: 'warning',
        heading: 'Files Rejected',
        text: `${rejectedFileCount} file(s) with names longer than 200 characters were rejected. Please rename and try again.`
      });
    }

    setFiles(prev => [...prev, ...validFiles.map(file => ({
      file,
      id: Date.now() + Math.random(),
      status: 'pending',
      progress: 0,
      meta: detectMeta(file.name),
    }))]);
  };

  const updateFileMeta = (id, field, value) => {
    setFiles(prev => prev.map(f =>
      f.id === id ? { ...f, meta: { ...f.meta, [field]: value } } : f
    ));
  };

  // Remove file
  const removeFile = (id) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  // Upload files
  const handleUpload = async () => {
    if (files.length === 0) {
      setAlert({
        variant: 'warning',
        heading: 'No Files',
        text: 'Please select files to upload.'
      });
      return;
    }

    if (!selectedCampaign) {
      setAlert({
        variant: 'warning',
        heading: 'No Campaign Selected',
        text: 'Please choose a campaign before uploading.'
      });
      return;
    }

    if (!selectedScorecard) {
      setAlert({
        variant: 'warning',
        heading: 'No Scorecard Selected',
        text: 'Please select a scorecard before uploading.'
      });
      return;
    }

    setUploading(true);

    const uploadType = files.length > 5 ? 'bulk' : 'single';
    // Generate unique jobId for this upload session
    const jobId = `job${Date.now()}${Math.random().toString(36).substring(2, 9)}`;

    // Validate that constructed filenames don't exceed 200 characters
    // (only relevant for files without metadata, since metadata-only names are always short)
    const tooLongFiles = files.filter((fileData) => {
      const uploadFile = prepareUploadFile(fileData.file, selectedCampaign.value, selectedScorecard.value, username, jobId, fileData.meta || {});
      return uploadFile.name.length > 200;
    });

    if (tooLongFiles.length > 0) {
      setAlert({
        variant: 'error',
        heading: 'Filename Too Long',
        text: `${tooLongFiles.length} file name(s) exceed 200 characters. Please shorten the file name or add metadata to replace it.`,
      });
      setUploading(false);
      return;
    }

    try {
      // Create job metadata before uploading files
      await createJobMetadata(
        jobId,
        selectedCampaign.value,
        selectedScorecard.value,
        username,
        files.length
      );

      for (let i = 0; i < files.length; i++) {
        const fileData = files[i];
        const uploadFile = prepareUploadFile(fileData.file, selectedCampaign.value, selectedScorecard.value, username, jobId, fileData.meta || {});

        // Update status to uploading
        setFiles(prev => prev.map(f =>
          f.id === fileData.id
            ? { ...f, status: 'uploading', progress: 0 }
            : f
        ));

        try {
          // Always use S3 presigned URL uploads to avoid payload limits
          await uploadRecordingS3(
            uploadFile,
            { campaign: selectedCampaign.value, scorecard: selectedScorecard.value },
            (percent) => {
              setFiles(prev => prev.map(f =>
                f.id === fileData.id ? { ...f, progress: percent } : f
              ));
            },
            uploadType
          );
          

          // Update status to completed
          setFiles(prev => prev.map(f =>
            f.id === fileData.id
              ? { ...f, status: 'completed', progress: 100 }
              : f
          ));
        } catch (error) {
          setFiles(prev => prev.map(f =>
            f.id === fileData.id
              ? { ...f, status: 'error', progress: 0 }
              : f
          ));
          console.error(`Error uploading ${fileData.file.name}:`, error);
        }
      }
      if (uploadType === 'bulk'){
        await triggerbulk()
      }

      const successCount = files.filter(f => f.status !== 'error').length;
      const errorCount = files.length - successCount;

      if (errorCount === 0) {
        setAlert({
          variant: 'success',
          heading: 'Upload Complete',
          text: `Successfully uploaded ${successCount} ${successCount === 1 ? 'file' : 'files'}.`
        });
      } else {
        setAlert({
          variant: 'warning',
          heading: 'Upload Partially Complete',
          text: `${successCount} files uploaded successfully, ${errorCount} failed.`
        });
      }

      // Refresh call list and navigate to dashboard
      await mutate('calls-list');
      sessionStorage.removeItem('calls_allData');
      sessionStorage.removeItem('calls_nextToken');
      navigate('/');

    } catch (error) {
      console.error('Upload error:', error);
      setAlert({
        variant: 'error',
        heading: 'Upload Failed',
        text: 'Failed to upload files. Please try again.'
      });
    } finally {
      setUploading(false);
    }
  };

  // Helper functions for filter management
  const addToArrayFilter = (filterKey, value, inputSetter) => {
    if (value.trim()) {
      setFilters(prev => ({
        ...prev,
        [filterKey]: [...prev[filterKey], value.trim()]
      }));
      inputSetter('');
    }
  };

  const removeFromArrayFilter = (filterKey, index) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: prev[filterKey].filter((_, i) => i !== index)
    }));
  };

  const updateFilter = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      queueIds: [],
      direction: '',
      userIds: [],
      wrapUpCodes: [],
      ani: '',
      dnis: '',
      divisionIds: []
    });
  };

  // Show preview of how many conversations will be fetched
  const handleShowPreview = async () => {
    if (!selectedCampaign) {
      setAlert({
        variant: 'warning',
        heading: 'No Queue Selected',
        text: 'Please choose a queue before fetching calls.'
      });
      return;
    }

    if (!selectedScorecard) {
      setAlert({
        variant: 'warning',
        heading: 'No Scorecard Selected',
        text: 'Please select a scorecard before fetching calls.'
      });
      return;
    }

    // If no date range is set, default to last 7 days
    let effectiveStartDate = startDate;
    let effectiveEndDate = endDate;

    if (!effectiveStartDate || !effectiveEndDate) {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 7);
      effectiveStartDate = start.toISOString().slice(0, 16);
      effectiveEndDate = end.toISOString().slice(0, 16);

      // Update state for display
      setStartDate(effectiveStartDate);
      setEndDate(effectiveEndDate);
    }

    // Validate date range
    const start = new Date(effectiveStartDate);
    const end = new Date(effectiveEndDate);
    const now = new Date();
    const diffInDays = (end - start) / (1000 * 60 * 60 * 24);

    // End date cannot be in the future
    if (end > now) {
      setAlert({
        variant: 'warning',
        heading: 'Please Check End Date',
        text: 'End date cannot be in the future.'
      });
      return;
    }

    // Date range must be at most 7 days
    if (diffInDays > 7) {
      setAlert({
        variant: 'warning',
        heading: 'Please Adjust Date Range',
        text: `Date range must be 7 days or less. Current range: ${diffInDays.toFixed(1)} days.`
      });
      return;
    }

    // Start date must be before end date
    if (diffInDays < 0) {
      setAlert({
        variant: 'warning',
        heading: 'Please Check Date Range',
        text: 'Start date must be before end date.'
      });
      return;
    }

    // Build filters object
    const activeFilters = {};
    if (filters.queueIds.length > 0) activeFilters.queueIds = filters.queueIds;
    if (filters.direction) activeFilters.direction = filters.direction;
    if (filters.userIds.length > 0) activeFilters.userIds = filters.userIds;
    if (filters.wrapUpCodes.length > 0) activeFilters.wrapUpCodes = filters.wrapUpCodes;
    if (filters.ani) activeFilters.ani = filters.ani;
    if (filters.dnis) activeFilters.dnis = filters.dnis;
    if (filters.divisionIds.length > 0) activeFilters.divisionIds = filters.divisionIds;

    // Show modal and start preview
    setShowPreviewModal(true);
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewData(null);

    try {
      // Call preview API
      const response = await previewInteractionCalls(
        selectedCampaign.value,
        selectedScorecard.value,
        effectiveStartDate,
        effectiveEndDate,
        Object.keys(activeFilters).length > 0 ? activeFilters : null
      );

      if (response.success && response.data) {
        setPreviewData(response.data);
      } else {
        setPreviewError(response.message || 'Failed to get preview');
      }
    } catch (error) {
      console.error('Preview error:', error);
      let errorMessage = 'Failed to get preview. Please try again.';

      if (error.message.includes('404')) {
        errorMessage = 'No conversations found matching the specified criteria.';
      } else if (error.message.includes('400')) {
        errorMessage = error.message;
      }

      setPreviewError(errorMessage);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Fetch calls from Genesys with filters (called after preview confirmation)
  const handleFetchInteractionCalls = async (prevData) => {
    // Close preview modal
    setShowPreviewModal(false);

    // If no date range is set, default to last 7 days
    let effectiveStartDate = startDate;
    let effectiveEndDate = endDate;

    if (!effectiveStartDate || !effectiveEndDate) {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 7);
      effectiveStartDate = start.toISOString().slice(0, 16);
      effectiveEndDate = end.toISOString().slice(0, 16);
    }

    setFetchingCalls(true);

    try {
      const now = new Date();
      const readableDate = now.toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const jobIdForMetadata = `daterange-${readableDate}`;

      // Create job metadata first
      await createJobMetadata(
        jobIdForMetadata,
        selectedCampaign.value,
        selectedScorecard.value,
        username,
        prevData.recordingCount || 0 // totalCalls will be incremented as files are uploaded
      );

      // Build filters object, excluding empty values
      const activeFilters = {};
      if (filters.queueIds.length > 0) activeFilters.queueIds = filters.queueIds;
      if (filters.direction) activeFilters.direction = filters.direction;
      if (filters.userIds.length > 0) activeFilters.userIds = filters.userIds;
      if (filters.wrapUpCodes.length > 0) activeFilters.wrapUpCodes = filters.wrapUpCodes;
      if (filters.ani) activeFilters.ani = filters.ani;
      if (filters.dnis) activeFilters.dnis = filters.dnis;
      if (filters.divisionIds.length > 0) activeFilters.divisionIds = filters.divisionIds;

      // Fetch the calls from Genesys with filters
      const response = await fetchInteractionCalls(
        selectedCampaign.value,
        selectedScorecard.value,
        effectiveStartDate,
        effectiveEndDate,
        Object.keys(activeFilters).length > 0 ? activeFilters : null,
        jobIdForMetadata
      );

      if (response.success) {
        const filterInfo = Object.keys(activeFilters).length > 0
          ? ` with ${Object.keys(activeFilters).length} filter(s) applied`
          : '';

        setAlert({
          variant: 'success',
          heading: 'Fetch Started',
          text: `Batch job started${filterInfo}. Expected recordings: ${response.data.expectedRecordings}. Processing in background.`
        });

        // Clear form
        setStartDate('');
        setEndDate('');
        clearAllFilters();

        // Navigate to jobs page
        setTimeout(() => {
          navigate(`/jobs/${jobIdForMetadata}`);
        }, 2000);
      } else {
        throw new Error(response.message || 'Failed to fetch calls');
      }

    } catch (error) {
      console.error('Fetch calls error:', error);

      let errorMessage = 'Failed to fetch calls. Please try again.';

      if (error.message.includes('404')) {
        errorMessage = 'No recordings found matching the specified criteria.';
      } else if (error.message.includes('400')) {
        errorMessage = error.message;
      }

      setAlert({
        variant: 'error',
        heading: 'Fetch Failed',
        text: errorMessage
      });
    } finally {
      setFetchingCalls(false);
    }
  };

  const clearAgents = () => {
      updateFilter('userIds', []);
      setAgentSearch(''); // Clear the text input as well
  };

  const filteredQueues = queueOptions.filter(q => 
    q.name.toLowerCase().includes(queueSearch.toLowerCase())
  );

  // Clear all files
  const clearFiles = () => {
    setFiles([]);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loadingCampaigns && campaigns.length === 0 && !campaignError) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading campaigns...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Upload Recordings</h1>
          <p className="text-gray-600 mt-1">
            Upload audio files for quality analysis and transcription
          </p>
        </div>
        
        <button
          onClick={() => navigate('/fileCopy')}
          className="btn-outline"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
          </svg>
          Cloud Connector
        </button>
      </div>

      {/* Mode Toggle */}
      <div className="card">
        <div className="flex space-x-2 p-1 bg-gray-100 rounded-lg">
          <button
            onClick={() => setUploadMode('file')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              uploadMode === 'file'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            File Upload
          </button>
          <button
            onClick={() => setUploadMode('genesys')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              uploadMode === 'genesys'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Fetch from Genesys
          </button>
          <button
            onClick={() => setUploadMode('genesysSingle')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              uploadMode === 'genesysSingle'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Fetch from Genesys (Single)
          </button>
        </div>
      </div>

      {/* Campaign Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Choose Campaign
        </label>
        {loadingCampaigns ? (
          <p className="text-sm text-gray-500">Loading campaigns...</p>
        ) : campaignError ? (
          <p className="text-sm text-red-600">{campaignError}</p>
        ) : (
          <select
            value={selectedCampaign?.value || ''}
            onChange={(e) => {
              const value = e.target.value;
              const campaign = campaigns.find((c) => c.value === value);
              setSelectedCampaign(campaign);
            }}
            className="select-field w-full"
          >
            <option value="" disabled>
              Select a campaign
            </option>
            {campaigns.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Scorecard Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Choose Scorecard
        </label>
        {loadingScorecards ? (
          <p className="text-sm text-gray-500">Loading scorecards...</p>
        ) : scorecardError ? (
          <p className="text-sm text-red-600">{scorecardError}</p>
        ) : (
          <select
            value={selectedScorecard?.value || ''}
            onChange={(e) => {
              const value = e.target.value;
              const sc = scorecards.find((s) => s.value === value);
              setSelectedScorecard(sc);
            }}
            className="select-field w-full"
          >
            <option value="" disabled>
              Select a scorecard
            </option>
            {scorecards.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Genesys Fetch Fields - Only show in genesys mode */}
      {uploadMode === 'genesys' && (
        <div className="card space-y-4">
          {/* Date Range */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Date Range (Required)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Defaults to 7 days ago if not specified
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Defaults to now if not specified
                </p>
              </div>
            </div>
            <div className="bg-blue-50 rounded-lg p-2 mt-2">
              <p className="text-xs text-blue-700">
                <strong>Date Range:</strong> {startDate && endDate ? (
                  (() => {
                    const days = ((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)).toFixed(1);
                    return `${days} days (max 7 days allowed)`;
                  })()
                ) : (
                  'Defaults to last 7 days. Max range: 7 days.'
                )}
              </p>
            </div>
          </div>

          {/* Filters Section */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900">Filters (Optional)</h3>
              <button
                onClick={clearAllFilters}
                className="text-xs text-cyan-600 hover:text-cyan-700 font-medium"
              >
                Clear All Filters
              </button>
            </div>

            <div className="space-y-4">
              {/* Direction Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Call Direction
                </label>
                <select
                  value={filters.direction}
                  onChange={(e) => updateFilter('direction', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                >
                  <option value="">All Directions</option>
                  <option value="inbound">Inbound Only</option>
                  <option value="outbound">Outbound Only</option>
                </select>
              </div>

              {/* 1. GENESYS QUEUE SEARCH */}
              <div className="relative">
                <div className="flex justify-between items-center mb-1">
                  <label className="text-xs font-semibold text-gray-500 uppercase">Genesys Queues</label>
                  {filters.queueIds.length > 0 && (
                    <button onClick={() => updateFilter('queueIds', [])} className="text-xs text-red-500 hover:underline">Clear All</button>
                  )}
                </div>
                <input
                  type="text"
                  className="w-full p-2 border rounded text-sm focus:ring-2 focus:ring-cyan-500 outline-none"
                  placeholder={fetchingFilters ? "Loading..." : "Search Queues..."}
                  value={queueSearch}
                  onChange={(e) => { setQueueSearch(e.target.value); setIsQueueOpen(true); }}
                  onFocus={() => setIsQueueOpen(true)}
                />
                {isQueueOpen && queueSearch && (
                  <div className="absolute z-50 w-full bg-white border mt-1 rounded shadow-xl max-h-40 overflow-y-auto">
                    {queueOptions.filter(q => q.name.toLowerCase().includes(queueSearch.toLowerCase())).map(queue => (
                      <div key={queue.id} className="p-2 hover:bg-cyan-50 cursor-pointer text-sm"
                        onClick={() => {
                          if (!filters.queueIds.includes(queue.id)) updateFilter('queueIds', [...filters.queueIds, queue.id]);
                          setQueueSearch(''); setIsQueueOpen(false);
                        }}>
                        {queue.name}
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-1 mt-2">
                  {filters.queueIds.map((id, i) => (
                    <span key={id} className="bg-cyan-100 text-cyan-700 px-2 py-1 rounded-full text-xs flex items-center">
                      {queueOptions.find(q => q.id === id)?.name || id}
                      <button onClick={() => removeFromArrayFilter('queueIds', i)} className="ml-1 font-bold">×</button>
                    </span>
                  ))}
                </div>
              </div>

              {/* 2. AGENT SEARCH (Uses isAgentOpen) */}
              <div className="relative mt-4">
                <div className="flex justify-between items-center mb-1">
                  <label className="text-xs font-semibold text-gray-500 uppercase">Agents</label>
                  {filters.userIds.length > 0 && (
                    <button onClick={() => { updateFilter('userIds', []); setAgentSearch(''); }} className="text-xs text-red-500 hover:underline">Clear All</button>
                  )}
                </div>
                <input
                  type="text"
                  className="w-full p-2 border rounded text-sm focus:ring-2 focus:ring-green-500 outline-none"
                  placeholder="Search Agents..."
                  value={agentSearch}
                  onChange={(e) => { setAgentSearch(e.target.value); setIsAgentOpen(true); }}
                  onFocus={() => setIsAgentOpen(true)}
                />
                {isAgentOpen && agentSearch && (
                  <div className="absolute z-50 w-full bg-white border mt-1 rounded shadow-xl max-h-40 overflow-y-auto">
                    {agentOptions.filter(a => a.name.toLowerCase().includes(agentSearch.toLowerCase())).map(agent => (
                      <div key={agent.id} className="p-2 hover:bg-green-50 cursor-pointer text-sm"
                        onClick={() => {
                          if (!filters.userIds.includes(agent.id)) updateFilter('userIds', [...filters.userIds, agent.id]);
                          setAgentSearch(''); setIsAgentOpen(false);
                        }}>
                        {agent.name}
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-1 mt-2">
                  {filters.userIds.map((id, i) => (
                    <span key={id} className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs flex items-center">
                      {agentOptions.find(a => a.id === id)?.name || id}
                      <button onClick={() => removeFromArrayFilter('userIds', i)} className="ml-1 font-bold">×</button>
                    </span>
                  ))}
                </div>
              </div>

              {/* 3. WRAP-UP CODE SEARCH (Uses isWrapupOpen) */}
              <div className="relative mt-4">
                <div className="flex justify-between items-center mb-1">
                  <label className="text-xs font-semibold text-gray-500 uppercase">Wrap-Up Codes</label>
                  {filters.wrapUpCodes.length > 0 && (
                    <button onClick={() => { updateFilter('wrapUpCodes', []); setWrapupSearch(''); }} className="text-xs text-red-500 hover:underline">Clear All</button>
                  )}
                </div>
                <input
                  type="text"
                  className="w-full p-2 border rounded text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                  placeholder="Search Codes..."
                  value={wrapupSearch}
                  onChange={(e) => { setWrapupSearch(e.target.value); setIsWrapupOpen(true); }}
                  onFocus={() => setIsWrapupOpen(true)}
                />
                {isWrapupOpen && wrapupSearch && (
                  <div className="absolute z-50 w-full bg-white border mt-1 rounded shadow-xl max-h-40 overflow-y-auto">
                    {wrapUpCodeOptions.filter(c => c.name.toLowerCase().includes(wrapupSearch.toLowerCase())).map(code => (
                      <div key={code.id} className="p-2 hover:bg-purple-50 cursor-pointer text-sm"
                        onClick={() => {
                          if (!filters.wrapUpCodes.includes(code.id)) updateFilter('wrapUpCodes', [...filters.wrapUpCodes, code.id]);
                          setWrapupSearch(''); setIsWrapupOpen(false);
                        }}>
                        {code.name}
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-1 mt-2">
                  {filters.wrapUpCodes.map((id, i) => (
                    <span key={id} className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs flex items-center">
                      {wrapUpCodeOptions.find(c => c.id === id)?.name || id}
                      <button onClick={() => removeFromArrayFilter('wrapUpCodes', i)} className="ml-1 font-bold">×</button>
                    </span>
                  ))}
                </div>
              </div>

              {/* ANI and DNIS Filters */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ANI (Caller Number)
                  </label>
                  <input
                    type="text"
                    value={filters.ani}
                    onChange={(e) => updateFilter('ani', e.target.value)}
                    placeholder="tel:+1234567890"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-gray-500">Format: tel:+[number]</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    DNIS (Dialed Number)
                  </label>
                  <input
                    type="text"
                    value={filters.dnis}
                    onChange={(e) => updateFilter('dnis', e.target.value)}
                    placeholder="tel:+0987654321"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-gray-500">Format: tel:+[number]</p>
                </div>
              </div>

              {/* Division IDs Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Division IDs
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={divisionIdInput}
                    onChange={(e) => setDivisionIdInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addToArrayFilter('divisionIds', divisionIdInput, setDivisionIdInput)}
                    placeholder="Enter division ID and press Enter"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                  <button
                    onClick={() => addToArrayFilter('divisionIds', divisionIdInput, setDivisionIdInput)}
                    className="px-4 py-2 bg-cyan-600 text-white rounded-md hover:bg-cyan-700"
                  >
                    Add
                  </button>
                </div>
                {filters.divisionIds.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {filters.divisionIds.map((id, index) => (
                      <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        {id}
                        <button
                          onClick={() => removeFromArrayFilter('divisionIds', index)}
                          className="ml-2 text-orange-600 hover:text-orange-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Fetch Button - Shows Preview First */}
          <button
            onClick={handleShowPreview}
            disabled={
              fetchingCalls ||
              !selectedCampaign ||
              !selectedScorecard ||
              previewLoading
            }
            className="w-full btn-success disabled:opacity-50"
          >
            {fetchingCalls ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Fetching Calls...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Preview & Fetch Calls from Genesys
              </>
            )}
          </button>
        </div>
      )}

      {uploadMode === 'genesysSingle' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Conversation ID
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={convoId}
              onChange={(e) => setConvoId(e.target.value)}
              placeholder="Enter user ID and press Enter"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            />
            <button
              disabled={isProcessing}
              onClick={async () => {
                console.log("Convo ID: ", convoId)
                  if (!selectedCampaign || !selectedScorecard) {
                    return setAlert({
                      variant: 'error',
                      heading: 'Empty Parameter',
                      text: "Make sure a Queue/Scorecard is selected."
                    })
                  } else if (convoId == ''){
                    return setAlert({
                      variant: 'error',
                      heading: 'Empty Parameter',
                      text: "Make sure Conversation ID has a valid value"
                    })
                  } else {
                    setIsProcessing(true);
                    try {
                      let res = await singleGenesysUpload(
                          selectedCampaign.value, 
                          selectedScorecard.value, 
                          convoId
                      );
                      if (res.results[0].status == "failed") {
                        setAlert({
                            variant: 'error',
                            heading: 'Upload Failed',
                            text: res.results[0].error || 'An unknown error occurred during the upload process.'
                        });
                      } else if (res.results[0].status == "success") {
                          setAlert({
                              variant: 'success',
                              heading: 'Upload Complete',
                              text: 'The Genesys recording has been successfully submitted for processing.'
                          });
                        }
                      // alert(`Success : ${res.message} and ${res.results[0].status} ${JSON.stringify(res)}`)

                  } catch (error) {
                      console.error("API Call Error:", error);
                      setAlert({
                          variant: 'error',
                          heading: 'Upload Failed',
                          text: error.message || 'An unknown error occurred during the upload process.'
                      });
                  } finally {
                      setIsProcessing(false);
                    }
                  }
                }
              }
            className={`px-4 py-2 text-white rounded-md transition-colors ${
                isProcessing 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-cyan-600 hover:bg-cyan-700'
              }`}
            >
              {isProcessing ? 'Processing...' : 'Process Call'}
            </button>
          </div>
        </div>
      )}

      {/* Upload Area - Only show in file mode */}
      {uploadMode === 'file' && (
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive
              ? 'border-cyan-500 bg-cyan-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDrop={handleDrop}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
        >
        <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
          <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        
        <div className="mt-4">
          <p className="text-lg font-medium text-gray-900">
            Drop audio files here or click to browse
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Supports WAV, MP3, M4A, FLAC files up to 100MB each
          </p>
        </div>
        
        <div className="mt-6">
          <label className="cursor-pointer">
            <input
              type="file"
              multiple
              accept="audio/*,.wav,.mp3,.m4a,.flac"
              onChange={handleFileChange}
              className="hidden"
            />
            <span className="btn-primary">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Select Files
            </span>
          </label>
        </div>
      </div>
      )}

      {/* File List - Only show in file mode */}
      {uploadMode === 'file' && files.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Files to Upload ({files.length})
            </h3>
            <div className="flex space-x-2">
              <button
                onClick={clearFiles}
                disabled={uploading}
                className="btn-outline disabled:opacity-50"
              >
                Clear All
              </button>
              <button
                onClick={handleUpload}
                disabled={
                  uploading ||
                  files.length === 0 ||
                  !selectedCampaign ||
                  !selectedScorecard
                }
                className="btn-primary disabled:opacity-50"
              >
                {uploading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Uploading...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Upload All
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {files.map((fileData) => (
              <FileItem
                key={fileData.id}
                fileData={fileData}
                onRemove={() => removeFile(fileData.id)}
                onMetaChange={(field, value) => updateFileMeta(fileData.id, field, value)}
                formatFileSize={formatFileSize}
              />
            ))}
          </div>
        </div>
      )}

      {/* Guidelines - Conditional based on mode */}
      {uploadMode === 'file' ? (
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Upload Guidelines
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>Ensure audio files are clear and have good quality</li>
                  <li>Files will be automatically transcribed and analyzed</li>
                  <li>Processing time depends on file size and duration</li>
                  <li>Use descriptive file names for easier identification</li>
                  <li>File names must be 200 characters or less</li>
                  <li>Supported formats: WAV, MP3, M4A, FLAC</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Genesys Fetch Guidelines
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>Date range is required (max 7 days, defaults to last 7 days)</li>
                  <li>Filters are optional - use them to narrow down your search</li>
                  <li>Queue IDs, User IDs, Wrap-Up Codes, and Division IDs support multiple values</li>
                  <li>Hard-coded filters always applied: Voice calls only, Answered calls only</li>
                  <li>ANI/DNIS filters must use format: tel:+[number]</li>
                  <li>Recordings are fetched asynchronously - check Jobs page for progress</li>
                  <li>Queue and Scorecard selection are required</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Genesys Preview Modal */}
      <GenesysPreviewModal
        open={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setPreviewData(null);
          setPreviewError(null);
        }}
        onConfirm={() => handleFetchInteractionCalls(previewData)}
        previewData={previewData}
        loading={previewLoading}
        error={previewError}
      />
    </div>
  );
};

// File Item Component
const FileItem = ({ fileData, onRemove, onMetaChange, formatFileSize }) => {
  const meta = fileData.meta || {};
  const anyDetected = meta.agentDetected || meta.guidDetected || meta.datetimeDetected;
  const [showMeta, setShowMeta] = useState(!!anyDetected);

  const getStatusIcon = () => {
    switch (fileData.status) {
      case 'pending':
        return (
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'uploading':
        return (
          <svg className="animate-spin w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        );
      case 'completed':
        return (
          <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getStatusText = () => {
    switch (fileData.status) {
      case 'pending':   return 'Pending';
      case 'uploading': return `Uploading... ${fileData.progress}%`;
      case 'completed': return 'Completed';
      case 'error':     return 'Failed';
      default:          return 'Unknown';
    }
  };

  // Compact badge shown after upload completes
  const metaBadge = () => {
    const parts = [];
    if (meta.agent)    parts.push(`Agent: ${meta.agent}`);
    if (meta.datetime) parts.push(meta.datetime.replace('T', ' '));
    if (meta.guid)     parts.push(`GUID: ${meta.guid}`);
    return parts.length > 0 ? parts.join(' · ') : null;
  };

  const isPending = fileData.status === 'pending';

  return (
    <div className="p-3 bg-gray-50 rounded-lg">
      {/* Main row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <div className="flex-shrink-0">{getStatusIcon()}</div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{fileData.file.name}</p>
            <p className="text-xs text-gray-500">
              {formatFileSize(fileData.file.size)} • {getStatusText()}
            </p>
            {!isPending && metaBadge() && (
              <p className="text-xs text-cyan-700 mt-0.5">{metaBadge()}</p>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
          {fileData.status === 'uploading' && (
            <div className="w-24">
              <div className="bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${fileData.progress}%` }}
                />
              </div>
            </div>
          )}

          {isPending && (
            <button
              onClick={() => setShowMeta(v => !v)}
              className="text-xs text-cyan-600 hover:text-cyan-800 font-medium whitespace-nowrap"
            >
              {showMeta ? '– Metadata' : '+ Add Metadata'}
            </button>
          )}

          {fileData.status !== 'uploading' && (
            <button
              onClick={onRemove}
              className="p-1 text-gray-400 hover:text-red-500 transition-colors"
              title="Remove file"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Metadata sub-row — only while pending */}
      {isPending && showMeta && (
        <div className="mt-3 grid grid-cols-3 gap-3 pl-8">
          {/* Agent */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Agent
              {meta.agentDetected && (
                <span className="ml-1 text-green-600">✓ in filename</span>
              )}
            </label>
            <input
              type="text"
              value={meta.agent || ''}
              disabled={meta.agentDetected}
              onChange={e => onMetaChange('agent', e.target.value)}
              placeholder="e.g. BobS"
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500 disabled:bg-gray-100 disabled:text-gray-500"
            />
            <p className="text-xs text-gray-400 mt-0.5">→ _AGENT_</p>
          </div>

          {/* Date & Time */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Call Date & Time
              {meta.datetimeDetected && (
                <span className="ml-1 text-green-600">✓ in filename</span>
              )}
            </label>
            <input
              type="datetime-local"
              value={meta.datetime || ''}
              disabled={meta.datetimeDetected}
              onChange={e => onMetaChange('datetime', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500 disabled:bg-gray-100 disabled:text-gray-500"
            />
            <p className="text-xs text-gray-400 mt-0.5">→ _DT_</p>
          </div>

          {/* GUID */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Call GUID / ID
              {meta.guidDetected && (
                <span className="ml-1 text-green-600">✓ in filename</span>
              )}
            </label>
            <input
              type="text"
              value={meta.guid || ''}
              disabled={meta.guidDetected}
              onChange={e => onMetaChange('guid', e.target.value)}
              placeholder="e.g. abc-123"
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500 disabled:bg-gray-100 disabled:text-gray-500"
            />
            <p className="text-xs text-gray-400 mt-0.5">→ _GUID_</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadRecordings;